<script setup>
import { computed, reactive, ref, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ActionFields from './ActionFields.vue';

const props = defineProps({
  automation: { type: Object, default: null },
  funnels: { type: Array, default: () => [] },
});
const emit = defineEmits(['save']);

const dialogRef = ref(null);

const blankForm = () => ({
  name: '',
  trigger_type: 'entered_stage',
  trigger_funnel_id: null,
  trigger_stage_id: null,
  trigger_status: 'won',
  trigger_idle_days: 7,
  conditions: [],
  actions: [],
});

const form = reactive(blankForm());

// Regras salvas antes de um campo existir chegam sem os objetos aninhados que
// o v-model exige; completa o que faltar em vez de estourar na edição.
const ensureActionDefaults = actions => {
  actions.forEach(action => {
    if (['move', 'duplicate', 'create_task'].includes(action.type)) {
      action.assignment = action.assignment || { mode: 'keep' };
    }
    if (action.type === 'duplicate') {
      action.copy = action.copy || {};
    }
    if (action.type === 'notify') {
      action.target = action.target || { mode: 'deal_assignee' };
    }
  });
};

watch(
  () => props.automation,
  automation => {
    Object.assign(form, blankForm());
    if (automation) {
      Object.assign(form, JSON.parse(JSON.stringify(automation)));
      form.trigger_status = form.trigger_status || 'won';
    }
    form.conditions = form.conditions || [];
    form.trigger_idle_days = form.trigger_idle_days || 7;
    ensureActionDefaults(form.actions);
  },
  { immediate: true }
);

const titleKey = computed(() =>
  props.automation
    ? 'AUTOMATION.CRM.FORM.EDIT_TITLE'
    : 'AUTOMATION.CRM.FORM.ADD_TITLE'
);

const triggerStages = computed(
  () =>
    props.funnels.find(funnel => funnel.id === form.trigger_funnel_id)
      ?.stages || []
);

const actionTypes = [
  'move',
  'duplicate',
  'create_task',
  'notify',
  'send_template',
  'webhook',
];

const addAction = type => {
  const blank = {
    move: {
      type: 'move',
      target_funnel_id: null,
      target_stage_id: null,
      assignment: { mode: 'keep' },
    },
    duplicate: {
      type: 'duplicate',
      target_funnel_id: null,
      target_stage_id: null,
      assignment: { mode: 'keep' },
      copy: {
        value_and_products: true,
        installments_and_payment: false,
        open_tasks: false,
      },
    },
    create_task: {
      type: 'create_task',
      title: '',
      description: '',
      due_in_days: null,
      assignment: { mode: 'keep' },
    },
    notify: { type: 'notify', target: { mode: 'deal_assignee' } },
    send_template: {
      type: 'send_template',
      inbox_id: null,
      template_name: '',
      template_language: 'pt_BR',
    },
    webhook: { type: 'webhook', url: '', secret: '' },
  }[type];
  form.actions.push(blank);
};

const addCondition = () => {
  form.conditions.push({ field: 'value', operator: 'greater_than', value: '' });
};

const canSave = computed(
  () => form.name && form.trigger_funnel_id && form.actions.length
);

const save = () => {
  const payload = JSON.parse(JSON.stringify(form));
  // Um gatilho por vez: o campo do outro modo vai nulo, senão a validação
  // do modelo recusa.
  if (form.trigger_type === 'status_changed') {
    payload.trigger_stage_id = null;
    payload.trigger_idle_days = null;
  } else {
    // Os outros são de etapa (no documento anexado, opcional); só o idle usa
    // os dias.
    payload.trigger_status = null;
    if (form.trigger_type !== 'stage_idle') payload.trigger_idle_days = null;
  }
  emit('save', payload);
};

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });

// Selects herdam o estilo global (_base.scss), que desenha a seta do
// dropdown como background contando com o padding-right dele. Sobrescrever o
// padding aqui escondia a seta.
const selectClass = 'w-full mb-0';
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    position="top"
    :title="$t(titleKey)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
  >
    <div class="flex flex-col w-full gap-4">
      <woot-input
        v-model="form.name"
        :label="$t('AUTOMATION.CRM.FORM.NAME')"
        type="text"
        :placeholder="$t('AUTOMATION.CRM.FORM.NAME_PLACEHOLDER')"
      />

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.TRIGGER') }}
        </span>
        <select v-model="form.trigger_type" :class="selectClass">
          <option value="entered_stage">
            {{ $t('AUTOMATION.CRM.FORM.ENTERED_STAGE') }}
          </option>
          <option value="status_changed">
            {{ $t('AUTOMATION.CRM.FORM.STATUS_CHANGED') }}
          </option>
          <option value="stage_idle">
            {{ $t('AUTOMATION.CRM.FORM.STAGE_IDLE') }}
          </option>
          <option value="document_attached">
            {{ $t('AUTOMATION.CRM.FORM.DOCUMENT_ATTACHED') }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.FUNNEL') }}
        </span>
        <select v-model="form.trigger_funnel_id" :class="selectClass">
          <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
            {{ funnel.name }}
          </option>
        </select>
      </label>

      <label
        v-if="form.trigger_type !== 'status_changed'"
        class="flex flex-col gap-1"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.STAGE') }}
        </span>
        <select v-model="form.trigger_stage_id" :class="selectClass">
          <option v-if="form.trigger_type === 'document_attached'" :value="null">
            {{ $t('AUTOMATION.CRM.FORM.ANY_STAGE') }}
          </option>
          <option
            v-for="stage in triggerStages"
            :key="stage.id"
            :value="stage.id"
          >
            {{ stage.name }}
          </option>
        </select>
      </label>

      <label
        v-if="form.trigger_type === 'stage_idle'"
        class="flex flex-col gap-1"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.IDLE_DAYS') }}
        </span>
        <input
          v-model.number="form.trigger_idle_days"
          type="number"
          min="1"
          class="w-full h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
      </label>

      <label
        v-if="form.trigger_type === 'status_changed'"
        class="flex flex-col gap-1"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.STATUS') }}
        </span>
        <select v-model="form.trigger_status" :class="selectClass">
          <option value="won">{{ $t('AUTOMATION.CRM.FORM.WON') }}</option>
          <option value="lost">{{ $t('AUTOMATION.CRM.FORM.LOST') }}</option>
        </select>
      </label>

      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.CONDITIONS') }}
        </span>
        <p class="mb-0 text-xs text-n-slate-10">
          {{ $t('AUTOMATION.CRM.FORM.CONDITIONS_HINT') }}
        </p>
        <div
          v-for="(condition, index) in form.conditions"
          :key="`cond-${index}`"
          class="flex flex-wrap items-center gap-2"
        >
          <select v-model="condition.field" class="mb-0 w-40">
            <option value="value">
              {{ $t('AUTOMATION.CRM.FORM.COND_VALUE') }}
            </option>
            <option value="origin">
              {{ $t('AUTOMATION.CRM.FORM.COND_ORIGIN') }}
            </option>
            <option value="assignee">
              {{ $t('AUTOMATION.CRM.FORM.COND_ASSIGNEE') }}
            </option>
            <option value="conversation_label">
              {{ $t('AUTOMATION.CRM.FORM.COND_LABEL') }}
            </option>
          </select>
          <select v-model="condition.operator" class="mb-0 w-40">
            <option v-if="condition.field === 'value'" value="greater_than">
              {{ $t('AUTOMATION.CRM.FORM.OP_GREATER') }}
            </option>
            <option v-if="condition.field === 'value'" value="less_than">
              {{ $t('AUTOMATION.CRM.FORM.OP_LESS') }}
            </option>
            <option
              v-if="['origin', 'assignee'].includes(condition.field)"
              value="equals"
            >
              {{ $t('AUTOMATION.CRM.FORM.OP_EQUALS') }}
            </option>
            <option v-if="condition.field === 'assignee'" value="not_equals">
              {{ $t('AUTOMATION.CRM.FORM.OP_NOT_EQUALS') }}
            </option>
            <option v-if="condition.field === 'assignee'" value="is_blank">
              {{ $t('AUTOMATION.CRM.FORM.OP_BLANK') }}
            </option>
            <option
              v-if="condition.field === 'conversation_label'"
              value="contains"
            >
              {{ $t('AUTOMATION.CRM.FORM.OP_CONTAINS') }}
            </option>
          </select>
          <input
            v-if="condition.operator !== 'is_blank'"
            v-model="condition.value"
            type="text"
            class="flex-1 h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
          <button
            type="button"
            class="text-xs bg-transparent border-none cursor-pointer text-n-ruby-11"
            @click="form.conditions.splice(index, 1)"
          >
            {{ $t('AUTOMATION.CRM.FORM.REMOVE') }}
          </button>
        </div>
        <div>
          <Button
            faded
            slate
            sm
            :label="$t('AUTOMATION.CRM.FORM.ADD_CONDITION')"
            @click="addCondition"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.CRM.FORM.ACTIONS') }}
        </span>
        <ActionFields
          v-for="(action, index) in form.actions"
          :key="index"
          v-model:action="form.actions[index]"
          :funnels="funnels"
          @remove="form.actions.splice(index, 1)"
        />
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="type in actionTypes"
            :key="type"
            faded
            slate
            sm
            :label="$t(`AUTOMATION.CRM.FORM.ADD_${type.toUpperCase()}`)"
            @click="addAction(type)"
          />
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          faded
          slate
          sm
          :label="$t('AUTOMATION.CRM.FORM.CANCEL')"
          @click="close"
        />
        <Button
          solid
          blue
          sm
          :label="$t('AUTOMATION.CRM.FORM.SAVE')"
          :disabled="!canSave"
          @click="save"
        />
      </div>
    </div>
  </Dialog>
</template>
